<template>
  <div class="admin-portal-page">
    <div class="portal-card">
      <div class="portal-logo"><img src="/assets/images/CHECK-APP.png" alt="Check-App" class="logo-img" /></div>
      <div class="portal-badge"><i class="fas fa-shield-alt mr-1"></i> {{ $t('adminPortal') }}</div>
      <h1 class="portal-title">{{ $t('adminLogin') }}</h1>

      <!-- Step 1: credentials -->
      <form v-if="step === 1" @submit.prevent="handleStep1" class="portal-form" novalidate>
        <div class="form-group">
          <label>Username</label>
          <input v-model="username" type="text" required autocomplete="username" />
        </div>
        <div class="form-group">
          <label>{{ $t('password') }}</label>
          <div class="password-wrap">
            <input v-model="password" :type="showPwd ? 'text' : 'password'" required autocomplete="current-password" />
            <button type="button" @click="showPwd = !showPwd" class="pwd-toggle">
              <i :class="showPwd ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
        </div>
        <div v-if="error" class="alert-error">{{ error }}</div>
        <button type="submit" class="btn-primary" :disabled="loading">
          <i v-if="loading" class="fas fa-spinner fa-spin mr-1"></i>
          {{ loading ? $t('loading') : 'Continue' }}
        </button>
      </form>

      <!-- Step 2: OTP -->
      <form v-else-if="step === 2" @submit.prevent="handleStep2" class="portal-form" novalidate>
        <div class="otp-info">
          <i class="fas fa-mobile-alt mr-2 text-blue-500"></i>
          {{ $t('otpSentMsg') }}
        </div>
        <div class="form-group">
          <label>{{ $t('enterOtp') }}</label>
          <input v-model="otp" type="text" maxlength="8" inputmode="numeric"
                 pattern="[0-9]*" class="otp-input" placeholder="000000" required />
        </div>
        <div v-if="error" class="alert-error">{{ error }}</div>
        <button type="submit" class="btn-primary" :disabled="loading">
          <i v-if="loading" class="fas fa-spinner fa-spin mr-1"></i>
          {{ loading ? $t('loading') : $t('verifyCode') }}
        </button>
        <button type="button" @click="step = 1" class="btn-back">← Back</button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route  = useRoute();
const REG_API = process.env.VUE_APP_REGISTRATION_API_URL || '/api/registration';

const step     = ref(1);
const username = ref('');
const password = ref('');
const showPwd  = ref(false);
const otp      = ref('');
const userEmail = ref('');
const loading  = ref(false);
const error    = ref('');

async function handleStep1() {
  loading.value = true; error.value = '';
  try {
    const res = await fetch(`${REG_API}/admin/login/step1/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.value, password: password.value }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');
    userEmail.value = data.user_email;
    step.value = 2;
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
}

async function handleStep2() {
  loading.value = true; error.value = '';
  try {
    const res = await fetch(`${REG_API}/admin/login/step2/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_email: userEmail.value, otp_code: otp.value }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Verification failed');
    sessionStorage.setItem('admin_portal_token', data.access);
    sessionStorage.setItem('admin_portal_user', JSON.stringify(data.user));
    router.push(`/${route.params.lang}/register/admin/pending`);
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.admin-portal-page { min-height:100vh; display:flex; align-items:center; justify-content:center;
  background:linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%); padding:20px; }
.portal-card { background:#fff; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.4);
  padding:40px; width:100%; max-width:420px; }
.portal-logo { text-align:center; margin-bottom:16px; }
.logo-img { height:60px; }
.portal-badge { display:inline-flex; align-items:center; background:#fef3c7; color:#92400e;
  border-radius:999px; padding:4px 14px; font-size:.75rem; font-weight:700;
  letter-spacing:.05em; margin-bottom:12px; }
.portal-title { font-size:1.4rem; font-weight:700; color:#0f172a; margin-bottom:24px; text-align:center; }
.portal-form { display:flex; flex-direction:column; gap:16px; }
.form-group { display:flex; flex-direction:column; gap:5px; }
.form-group label { font-size:.875rem; font-weight:600; color:#374151; }
.form-group input { border:1.5px solid #d1d5db; border-radius:8px; padding:10px 14px;
  font-size:.9rem; transition:border-color .2s; }
.form-group input:focus { outline:none; border-color:#0f172a; }
.password-wrap { position:relative; }
.password-wrap input { width:100%; padding-right:40px; box-sizing:border-box; }
.pwd-toggle { position:absolute; right:10px; top:50%; transform:translateY(-50%);
  background:none; border:none; cursor:pointer; color:#6b7280; }
.otp-info { background:#eff6ff; border:1px solid #bfdbfe; border-radius:8px; padding:12px;
  font-size:.875rem; color:#1e40af; }
.otp-input { letter-spacing:.4em; font-size:1.4rem; text-align:center; font-weight:700; }
.alert-error { background:#fef2f2; border:1px solid #fca5a5; color:#b91c1c;
  border-radius:8px; padding:10px; font-size:.875rem; }
.btn-primary { background:#0f172a; color:#fff; border:none; border-radius:10px;
  padding:13px; font-size:1rem; font-weight:600; cursor:pointer; display:flex;
  align-items:center; justify-content:center; transition:background .2s; }
.btn-primary:hover:not(:disabled) { background:#1e3a5f; }
.btn-primary:disabled { opacity:.6; cursor:not-allowed; }
.btn-back { background:none; border:none; color:#6b7280; cursor:pointer; font-size:.875rem;
  text-align:center; padding:4px; }
</style>
